/**
 * MerlinAnalysisBlocks.jsx — UI blocks RÉUTILISABLES extraits de
 * UserProfileAnalysis.jsx (analyse profil Merlin admin) pour que Studio
 * puisse rendre exactement le même look avec sa propre data shape.
 *
 * Exports utilitaires : Stars, Dropdown, MiniDropdown, BIO_DIM_META
 * Exports blocs : AvisDeMerlinBlock, BioAnalysisBlock, AudienceSignalsBlock
 *
 * Chaque bloc prend la data en props (zero fetch interne). C'est au caller
 * (Merlin ou Studio) d'adapter sa data au shape attendu.
 */

import { useState } from 'react'

// ═══════════════════════════════════════════════════════════════
// UTILITY COMPONENTS
// ═══════════════════════════════════════════════════════════════

export function Stars({ score, max = 5, size = 14 }) {
  const filled = Math.round((Number(score) / 10) * max)
  return (
    <span className="inline-flex items-center gap-px">
      {Array.from({ length: max }).map((_, i) => (
        <span key={i}
          style={{ fontSize: size, color: i < filled ? '#f59e0b' : 'rgba(0,0,0,0.1)', transition: 'color 0.3s' }}>
          ✦
        </span>
      ))}
    </span>
  )
}

export function Dropdown({ title, icon, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-xl overflow-hidden bg-white" style={{ border: '1px solid rgba(124,58,237,0.10)' }}>
      <button onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center gap-2.5 text-left transition-colors hover:bg-purple-50/30">
        {icon && <span className="text-sm">{icon}</span>}
        <span className="text-sm font-bold text-ink flex-1">{title}</span>
        {badge}
        <span className={`text-xs text-purple-300 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>▶</span>
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  )
}

const MINI_COLORS = {
  green:  { bg: 'rgba(34,197,94,0.04)',  border: 'rgba(34,197,94,0.15)',  text: '#16a34a', icon: '#22c55e' },
  orange: { bg: 'rgba(245,158,11,0.04)', border: 'rgba(245,158,11,0.15)', text: '#d97706', icon: '#f59e0b' },
  purple: { bg: 'rgba(124,58,237,0.04)', border: 'rgba(124,58,237,0.15)', text: '#7c3aed', icon: '#a78bfa' },
  red:    { bg: 'rgba(239,68,68,0.04)',  border: 'rgba(239,68,68,0.15)',  text: '#dc2626', icon: '#ef4444' },
  blue:   { bg: 'rgba(59,130,246,0.04)', border: 'rgba(59,130,246,0.15)', text: '#2563eb', icon: '#3b82f6' },
}

export function MiniDropdown({ icon, label, color = 'purple', children, defaultOpen = false, rightSlot = null }) {
  const [open, setOpen] = useState(defaultOpen)
  const c = MINI_COLORS[color] || MINI_COLORS.purple
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${c.border}`, background: c.bg }}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors hover:opacity-80">
        <span className="text-xs font-bold" style={{ color: c.icon }}>{icon}</span>
        <span className="text-sm font-bold flex-1" style={{ color: c.text }}>{label}</span>
        {rightSlot}
        <span className="text-xs transition-transform" style={{ color: c.text, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
      </button>
      {open && <div className="px-3 pb-2.5 pt-0.5">{children}</div>}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════

export const BIO_DIM_META = {
  clarte:      { emoji: '🔍', label: 'Clarté',       short: 'On comprend ton activité en 2s' },
  accroche:    { emoji: '🪝', label: 'Accroche',     short: 'La 1ère ligne capte' },
  credibilite: { emoji: '🏆', label: 'Crédibilité',  short: 'Preuves d\'autorité' },
  cta:         { emoji: '👉', label: 'CTA',           short: 'Appel à l\'action clair' },
}

// ═══════════════════════════════════════════════════════════════
// BLOCK : L'AVIS DE MERLIN
// Props : { portrait, scoreGlobal, forces, faiblesses, formats, direction, icon }
// ═══════════════════════════════════════════════════════════════

export function AvisDeMerlinBlock({ portrait, scoreGlobal, forces, faiblesses, formats, direction, icon, defaultOpen = true }) {
  if (!portrait) return null
  return (
    <Dropdown
      title="L'avis de Merlin"
      icon={icon || '🪄'}
      badge={scoreGlobal != null ? <Stars score={scoreGlobal} size={11} /> : null}
      defaultOpen={defaultOpen}
    >
      <p className="text-sm text-ink leading-[1.7] mb-4">{portrait}</p>

      <div className="space-y-2">
        {Array.isArray(forces) && forces.length > 0 && (
          <MiniDropdown icon="✦" label={`${forces.length} points forts`} color="green">
            {forces.slice(0, 6).map((f, i) => (
              <p key={i} className="text-sm text-ink/60 leading-relaxed mb-1.5 last:mb-0 pl-3"
                style={{ borderLeft: '2px solid rgba(34,197,94,0.3)' }}>{f}</p>
            ))}
          </MiniDropdown>
        )}

        {Array.isArray(faiblesses) && faiblesses.length > 0 && (
          <MiniDropdown icon="→" label={`${faiblesses.length} axes d'amélioration`} color="orange">
            {faiblesses.slice(0, 6).map((raw, i) => {
              let f = raw
              if (typeof f === 'string' && f.trim().startsWith('{')) {
                try { f = JSON.parse(f) } catch {}
              }
              const isObj = f && typeof f === 'object' && f.label
              const label = isObj ? f.label : f
              const conseils = isObj ? (Array.isArray(f.conseils) ? f.conseils : []) : []
              return (
                <details key={i} className="group/fb mb-1.5 last:mb-0" open={conseils.length > 0}>
                  <summary className="text-sm font-bold text-ink/70 cursor-pointer select-none flex items-center gap-1.5 py-0.5 hover:text-ink/90 transition-colors">
                    <span className="text-orange text-xs transition-transform group-open/fb:rotate-90">▶</span>
                    <span>{label}</span>
                  </summary>
                  {conseils.length > 0 && (
                    <div className="ml-4 mt-1.5 space-y-1">
                      {conseils.map((c, ci) => (
                        <p key={ci} className="text-sm text-ink/55 leading-relaxed pl-2.5"
                          style={{ borderLeft: '2px solid rgba(249,115,22,0.25)' }}>{c}</p>
                      ))}
                    </div>
                  )}
                </details>
              )
            })}
          </MiniDropdown>
        )}

        {Array.isArray(formats) && formats.length > 0 && (
          <MiniDropdown icon="🧪" label={`${formats.length} formats à tester`} color="purple">
            <div className="flex flex-wrap gap-1.5">
              {formats.map((f, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ background: 'rgba(124,58,237,0.06)', color: '#6d28d9', border: '1px solid rgba(124,58,237,0.15)' }}>
                  {f}
                </span>
              ))}
            </div>
          </MiniDropdown>
        )}
      </div>

      {direction && (
        <div className="mt-3 p-3 rounded-lg"
          style={{ background: 'linear-gradient(135deg, rgba(254,130,4,0.06), rgba(245,158,11,0.04))', border: '1px solid rgba(254,130,4,0.20)' }}>
          <p className="text-[10px] uppercase tracking-wider font-bold text-orange-700 mb-1">🎯 Direction stratégique</p>
          <p className="text-xs text-ink leading-relaxed">{direction}</p>
        </div>
      )}
    </Dropdown>
  )
}

// ═══════════════════════════════════════════════════════════════
// BLOCK : ANALYSE DE TA BIO
// Props : { bio, profile, competitors, bioSugs, icon }
// bio : { actuel, note_globale, dimensions, forces, faiblesses, suggestion_cle }
// ═══════════════════════════════════════════════════════════════

export function BioAnalysisBlock({ bio, profile, competitors, bioSugs, icon }) {
  if (!bio && !competitors?.length && !bioSugs?.length) return null
  return (
    <Dropdown
      title="Analyse de ta bio"
      icon={icon || '📝'}
      badge={bio?.note_globale != null ? <Stars score={bio.note_globale} size={11} /> : null}
    >
      {/* Bio actuelle */}
      {(profile?.bio || bio?.actuel) && (
        <div className="rounded-lg p-3.5 mb-4"
          style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.05)' }}>
          <p className="text-xs font-bold text-ink-faint uppercase tracking-wider mb-1.5">Bio actuelle</p>
          <p className="text-sm text-ink leading-[1.8] whitespace-pre-line">{profile?.bio || bio?.actuel}</p>
        </div>
      )}

      {/* 4 dimensions */}
      {bio?.dimensions && (
        <div className="space-y-1.5 mb-5">
          {Object.entries(BIO_DIM_META).map(([key, meta]) => {
            const dim = bio.dimensions[key]
            if (!dim?.note) return null
            const tint = dim.note >= 8 ? 'rgba(34,197,94,0.04)' : dim.note >= 6 ? 'rgba(245,158,11,0.04)' : 'rgba(239,68,68,0.04)'
            const border = dim.note >= 8 ? 'rgba(34,197,94,0.10)' : dim.note >= 6 ? 'rgba(245,158,11,0.10)' : 'rgba(239,68,68,0.10)'
            const shortVerdict = dim.detail ? dim.detail.split('.')[0].substring(0, 80) : meta.short
            return (
              <details key={key} className="group/dim rounded-lg overflow-hidden" style={{ background: tint, border: `1px solid ${border}` }}>
                <summary className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none list-none">
                  <span className="text-sm">{meta.emoji}</span>
                  <span className="text-sm font-bold text-ink">{meta.label}</span>
                  <Stars score={dim.note} size={11} />
                  <span className="text-xs text-ink/40 flex-1 truncate ml-1">{shortVerdict}</span>
                  <span className="text-xs text-ink/25 transition-transform duration-200 group-open/dim:rotate-90 flex-shrink-0">▶</span>
                </summary>
                {dim.detail && (
                  <div className="px-3 pb-3 pt-0.5">
                    <p className="text-xs text-ink/55 leading-[1.7]">{dim.detail}</p>
                  </div>
                )}
              </details>
            )
          })}
        </div>
      )}

      {/* Forces + Faiblesses */}
      {(bio?.forces?.length > 0 || bio?.faiblesses?.length > 0) && (
        <div className="grid grid-cols-2 gap-4 mb-5">
          {bio?.forces?.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: '#16a34a' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#22c55e' }} /> Points forts
              </p>
              {bio.forces.map((s, i) => (
                <p key={i} className="text-xs text-ink/60 leading-relaxed mb-1.5 pl-2.5"
                  style={{ borderLeft: '2px solid rgba(34,197,94,0.25)' }}>{s}</p>
              ))}
            </div>
          )}
          {bio?.faiblesses?.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: '#d97706' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f59e0b' }} /> À améliorer
              </p>
              {bio.faiblesses.map((w, i) => (
                <p key={i} className="text-xs text-ink/60 leading-relaxed mb-1.5 pl-2.5"
                  style={{ borderLeft: '2px solid rgba(245,158,11,0.25)' }}>{w}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Suggestion clé */}
      {bio?.suggestion_cle && (
        <div className="p-3 rounded-lg mb-4"
          style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(254,130,4,0.04))', border: '1px solid rgba(124,58,237,0.20)' }}>
          <p className="text-[10px] uppercase tracking-wider font-bold text-purple-700 mb-1">💡 Action prioritaire</p>
          <p className="text-sm font-bold text-ink leading-snug">{bio.suggestion_cle}</p>
        </div>
      )}

      {/* Bios suggérées par Merlin */}
      {Array.isArray(bioSugs) && bioSugs.length > 0 && (
        <MiniDropdown icon="✨" label={`Bios suggérées par Merlin (${bioSugs.length})`} color="purple">
          <div className="space-y-2 pt-1">
            {bioSugs.map((s, i) => (
              <div key={i} className="p-3 rounded-md"
                style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(124,58,237,0.10)' }}>
                <p className="text-xs text-ink leading-relaxed whitespace-pre-line mb-2">{s.version}</p>
                {s.explication && (
                  <p className="text-[10px] text-ink-muted italic">→ {s.explication}</p>
                )}
              </div>
            ))}
          </div>
        </MiniDropdown>
      )}

      {/* Bios concurrentes */}
      {Array.isArray(competitors) && competitors.length > 0 && (
        <div className="mt-2">
          <MiniDropdown icon="🎯" label={`Bios de comptes similaires (${competitors.length})`} color="blue">
            <div className="space-y-2 pt-1">
              {competitors.map((c, i) => (
                <div key={i} className="p-3 rounded-md bg-white border border-border">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <a href={c.lien_profil || '#'} target="_blank" rel="noreferrer"
                      className="text-xs font-bold text-ink hover:text-orange transition-colors">
                      {c.username}
                    </a>
                    {c.follower_count > 0 && (
                      <span className="text-[10px] text-ink-muted">{c.follower_count.toLocaleString('fr-FR')} abos</span>
                    )}
                  </div>
                  <p className="text-xs text-ink/65 leading-relaxed whitespace-pre-line">{c.bio}</p>
                </div>
              ))}
            </div>
          </MiniDropdown>
        </div>
      )}
    </Dropdown>
  )
}

// ═══════════════════════════════════════════════════════════════
// BLOCK : SIGNAUX D'AUDIENCE
// Props : { signals, icon }
// signals : { peur_profonde: [{label, pct}], desir_tangible: [...], ... }
// ═══════════════════════════════════════════════════════════════

const SIGNAL_TABS = {
  douleurs: {
    label: '😰 Souffrance',
    color: '#dc2626',
    bg: 'rgba(239,68,68,0.06)',
    keys: ['peur_profonde', 'douleur_quotidienne', 'honte_secrete', 'ennemi_visible', 'ennemi_interieur', 'ennemi_systemique'],
  },
  desirs: {
    label: '✨ Aspiration',
    color: '#16a34a',
    bg: 'rgba(34,197,94,0.06)',
    keys: ['desir_tangible', 'desir_identitaire'],
  },
  tribu: {
    label: '👥 Identité',
    color: '#2563eb',
    bg: 'rgba(59,130,246,0.06)',
    keys: ['tribu_identitaire', 'tribu_aspirationnelle', 'emotion_dominante'],
  },
  viral: {
    label: '🔥 Performance',
    color: '#d97706',
    bg: 'rgba(245,158,11,0.06)',
    keys: ['mecanisme_viral'],
  },
}

const SIGNAL_LABELS = {
  peur_profonde: 'A peur de',
  douleur_quotidienne: 'Souffre de',
  honte_secrete: 'A honte de',
  ennemi_visible: 'Désigne comme ennemi',
  ennemi_interieur: 'Bloque par',
  ennemi_systemique: 'Doute du système',
  desir_tangible: 'Veut obtenir',
  desir_identitaire: 'Veut devenir',
  tribu_identitaire: 'Se définit comme',
  tribu_aspirationnelle: 'Admire les',
  emotion_dominante: 'Ressent surtout',
  mecanisme_viral: 'Ce qui performe',
}

export function AudienceSignalsBlock({ signals, icon }) {
  const [activeTab, setActiveTab] = useState('douleurs')
  if (!signals || Object.keys(signals).length === 0) return null
  const tab = SIGNAL_TABS[activeTab]
  const activeKeys = tab.keys.filter(k => Array.isArray(signals[k]) && signals[k].length > 0)
  return (
    <Dropdown
      title="Signaux d'audience"
      icon={icon || '🎯'}
      defaultOpen={false}
    >
      <div className="flex gap-1 mb-3 overflow-x-auto -mx-1 px-1 pb-1">
        {Object.entries(SIGNAL_TABS).map(([k, t]) => (
          <button key={k} onClick={() => setActiveTab(k)}
            className={`shrink-0 text-[11px] font-bold px-2.5 py-1.5 rounded-md whitespace-nowrap transition-colors ${activeTab === k ? 'text-white' : 'text-ink-muted hover:bg-ink/10'}`}
            style={activeTab === k ? { background: t.color } : { background: 'rgba(15,16,64,0.05)' }}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {activeKeys.length === 0 && (
          <p className="text-xs text-ink-muted italic">Pas de signaux détectés sur cette catégorie.</p>
        )}
        {activeKeys.map(key => (
          <div key={key} className="p-3 rounded-lg" style={{ background: tab.bg, border: `1px solid ${tab.color}1a` }}>
            <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: tab.color }}>
              {SIGNAL_LABELS[key] || key}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {signals[key].slice(0, 6).map((cluster, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full text-[11px] font-bold text-ink flex items-center gap-1.5 bg-white border border-border">
                  {cluster.label}
                  {cluster.pct != null && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full text-white"
                      style={{ background: tab.color }}>
                      {cluster.pct}%
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Dropdown>
  )
}
